import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  signal,
  DestroyRef,
  computed,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { combineLatest, filter, Observable, pairwise, Subject, Subscription, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ButtonTheme,
  ButtonType,
  IconPosition,
  ModalButtons,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components';
import { AppState, NativeBridgeService } from '@fcom/core';
import { LoginStatus, Profile } from '@fcom/core-api/login/interfaces/login.interface';
import { loginStatus, profile } from '@fcom/core/selectors/login.selector';
import { finShare } from '@fcom/rx';

import { EmailVerificationResult } from '../../interfaces/loyalty-email-change-verification-mfa-form.interface';
import { TwoFactorAuthService } from '../../services/two-factor-auth.service';
import { GtmService } from '../../../gtm/services/gtm.service';
import { ElementActions, ElementTypes, GaContext } from '../../../interfaces/gtm.interface';
import { AccountSecurityService } from '../../services/account-security.service';

export enum SoftEnforceMfaFlowStep {
  ImproveSecurity = 'improveSecurity',
  EmailVerification = 'emailVerification',
  MfaEnabled = 'mfaEnabled',
}

export enum SoftEnforceMfaError {
  Send2faEmailVerificationCode = 'send2faEmailVerificationCode',
  EnableEmailMfa = 'enableEmailMfa',
}

@Component({
  selector: 'fin-soft-enforce-mfa',
  templateUrl: 'soft-enforce-mfa.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SoftEnforceMfaComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<AppState>>(Store);
  private destroyRef = inject(DestroyRef);
  private gtmService = inject(GtmService);
  private nativeBridgeService = inject(NativeBridgeService);
  // AccountSecurityService requests MFA status when constructed, so do not instantiate it inside the app WebView.
  // @ts-expect-error - Intentionally injected to instantiate service at component load
  private _accountSecurityService = this.nativeBridgeService.isInsideReactNativeWebView
    ? undefined
    : inject(AccountSecurityService);
  public twoFactorAuthService = inject(TwoFactorAuthService);

  readonly SoftEnforceMfaFlowStep = SoftEnforceMfaFlowStep;
  readonly SoftEnforceMfaError = SoftEnforceMfaError;
  readonly ModalButtons = ModalButtons;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;

  readonly trackElementNamePrefix = 'soft-enforce-2fa-';

  readonly modalTitleLabelKey = computed(() => {
    switch (this.currentStep()) {
      case SoftEnforceMfaFlowStep.EmailVerification:
        return 'login.softEnforceMfa.modal.checkYourEmailTitle';
      case SoftEnforceMfaFlowStep.MfaEnabled:
        return 'login.softEnforceMfa.modal.twoFactorAuthEnabledTitle';
      case SoftEnforceMfaFlowStep.ImproveSecurity:
      default:
        return 'login.softEnforceMfa.modal.improveYourAccountSecurityTitle';
    }
  });

  readonly cancelButtonTextLabelKey = computed(() => {
    switch (this.currentStep()) {
      case SoftEnforceMfaFlowStep.EmailVerification:
        return 'actions.skip';
      case SoftEnforceMfaFlowStep.MfaEnabled:
        return 'actions.close';
      case SoftEnforceMfaFlowStep.ImproveSecurity:
      default:
        return 'login.softEnforceMfa.modal.remindMeNextTimeButton';
    }
  });

  readonly confirmButtonTextLabelKey = computed(() => {
    switch (this.currentStep()) {
      case SoftEnforceMfaFlowStep.EmailVerification:
        return 'actions.verify';
      case SoftEnforceMfaFlowStep.MfaEnabled:
        return '';
      case SoftEnforceMfaFlowStep.ImproveSecurity:
      default:
        return 'login.softEnforceMfa.modal.enableNowButton';
    }
  });

  readonly currentStepToNext = computed<Record<SoftEnforceMfaFlowStep, SoftEnforceMfaFlowStep | null>>(() => ({
    [SoftEnforceMfaFlowStep.ImproveSecurity]: this.isEmailVerified()
      ? SoftEnforceMfaFlowStep.MfaEnabled
      : SoftEnforceMfaFlowStep.EmailVerification,
    [SoftEnforceMfaFlowStep.EmailVerification]: SoftEnforceMfaFlowStep.MfaEnabled,
    [SoftEnforceMfaFlowStep.MfaEnabled]: null,
  }));

  modalOpen = signal(false);
  currentStep = signal(SoftEnforceMfaFlowStep.ImproveSecurity);
  isEmailVerified = signal(false);
  requestError = signal<SoftEnforceMfaError | undefined>(undefined);

  profile$: Observable<Profile>;
  submitEmailVerification$ = new Subject<void>();

  private requestSubscription: Subscription = undefined;

  ngOnInit(): void {
    // Keep the component host stable for hydration while disabling for RN app.
    if (this.nativeBridgeService.isInsideReactNativeWebView) {
      return;
    }

    this.profile$ = this.store$.pipe(profile(), finShare());

    this.store$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        loginStatus(),
        filter(
          (currentLoginStatus) =>
            currentLoginStatus === LoginStatus.NOT_LOGGED_IN || currentLoginStatus === LoginStatus.LOGGED_IN
        ),
        pairwise(),
        filter(([previous, current]) => previous === LoginStatus.NOT_LOGGED_IN && current === LoginStatus.LOGGED_IN),
        switchMap(() => combineLatest([this.profile$.pipe(take(1)), this.twoFactorAuthService.enabledStatus$])),
        filter(([memberProfile, enabledStatusData]) => memberProfile && !enabledStatusData.isInitial),
        take(1)
      )
      .subscribe({
        next: ([memberProfile, enabledStatusData]) => {
          if (enabledStatusData.isEnabled || memberProfile.memberStatus === 'TEST_MEMBER') {
            return;
          }

          this.isEmailVerified.set(memberProfile.emailVerified);
          this.modalOpen.set(true);

          this.gtmService.trackElement(
            `${this.trackElementNamePrefix}modal`,
            GaContext.FINNAIR_PLUS,
            ElementTypes.NOTIFICATION,
            'verification-offered',
            ElementActions.VIEW
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
  }

  executeNextAction(): void {
    this.requestSubscription?.unsubscribe();
    this.requestError.set(undefined);

    if (this.currentStep() === SoftEnforceMfaFlowStep.ImproveSecurity) {
      this.gtmService.trackElement(
        `${this.trackElementNamePrefix}enable-start`,
        GaContext.FINNAIR_PLUS,
        ElementTypes.BUTTON,
        'verification-started',
        ElementActions.CLICK
      );

      if (this.isEmailVerified()) {
        this.requestSubscription = this.twoFactorAuthService.enableEmailMfa().subscribe({
          next: () => {
            this.proceedToNextStep();
          },
          error: () => {
            this.requestError.set(SoftEnforceMfaError.EnableEmailMfa);
          },
        });
      } else {
        this.requestSubscription = this.twoFactorAuthService.send2faEmailVerificationCode().subscribe({
          next: () => {
            this.proceedToNextStep();
          },
          error: () => {
            this.requestError.set(SoftEnforceMfaError.Send2faEmailVerificationCode);
          },
        });
      }
    } else if (this.currentStep() === SoftEnforceMfaFlowStep.EmailVerification) {
      this.submitEmailVerification$.next();
    }
  }

  onEmailVerificationResult(emailVerificationResult: EmailVerificationResult): void {
    if (emailVerificationResult.success) {
      this.proceedToNextStep();
    }
  }

  onCloseRequestError(): void {
    this.requestError.set(undefined);
  }

  onWhatIsTwoFactorAuthButtonBeforeNavigation(event: Event): void {
    this.gtmService.trackElement(
      `${this.trackElementNamePrefix}more-info`,
      GaContext.FINNAIR_PLUS,
      ElementTypes.LINK,
      (event.target as HTMLAnchorElement).href,
      ElementActions.CLICK
    );
  }

  onClickWithPossibleInternalLink(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      this.closeModal();
    }
  }

  closeModal(): void {
    this.modalOpen.set(false);

    const trackingElementName =
      this.currentStep() === SoftEnforceMfaFlowStep.ImproveSecurity
        ? `${this.trackElementNamePrefix}next-time`
        : this.currentStep() === SoftEnforceMfaFlowStep.EmailVerification
          ? `${this.trackElementNamePrefix}cancel`
          : undefined;

    if (trackingElementName) {
      this.gtmService.trackElement(
        trackingElementName,
        GaContext.FINNAIR_PLUS,
        ElementTypes.BUTTON,
        this.currentStep() === SoftEnforceMfaFlowStep.EmailVerification ? 'verification-skipped' : undefined,
        ElementActions.CLICK
      );
    }
  }

  private proceedToNextStep(): void {
    const nextStep = this.currentStepToNext()[this.currentStep()];
    this.currentStep.set(nextStep);

    if (nextStep === SoftEnforceMfaFlowStep.EmailVerification) {
      this.gtmService.trackElement(
        `${this.trackElementNamePrefix}code-request`,
        GaContext.FINNAIR_PLUS,
        ElementTypes.MODAL,
        'verification-pending-code',
        ElementActions.VIEW
      );
    } else if (nextStep === SoftEnforceMfaFlowStep.MfaEnabled) {
      this.gtmService.trackElement(
        `${this.trackElementNamePrefix}enable-success`,
        GaContext.FINNAIR_PLUS,
        ElementTypes.MODAL,
        'verification-finished',
        ElementActions.VIEW
      );
    }
  }
}
